//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// chrome-menu-drawer imports
import { IContainer, IProps, IState } from "./ChromeMenuDrawer.d";
import { chromeMenuDrawerFragments } from "./ChromeMenuDrawer.gql";
import * as s from "./ChromeMenuDrawer.styles";
// other imports
import { AvatarPerson } from "@components/avatar";
import { ChromeDrawer } from "@components/chrome-drawer";
import { ChromeFeedback } from "@components/chrome-feedback";
import { LogoutBtn } from "@components/logout-btn";
import { H2 } from "@ui";
// util
import { maybeGetPrimaryTeamFromOrg } from "@util/team.util";
import { getCurrentOrg } from "@util/org.util";
import { filterAndSortCategories } from "@util/event-category-coloring.util";
import { sortDataSources } from "@util/data-source.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { baseUrl } from "@clockwise/client-commons/src/constants/help-center";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { isPrimarySubscriptionFreePlan } from "@util/pricing.util";
// state
import {
  setMenuDrawerOpen,
  setSettingsDialogOpen,
  setActiveViewAutopilotList,
  setActiveViewCalendarProfile,
} from "@actions/chrome-extension.actions";
import { toggleExpanded } from "@actions/settings.actions";
import { setPricingDialogOpen } from "@actions/payments.actions";
import { ISettingsState } from "@reducers/settings.reducer";
import { IReduxState } from "@reducers/root.reducer";
// material-ui
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  SvgIcon,
} from "@material-ui/core";
import type { ListItemTextProps as MuiListItemTextProps } from "@material-ui/core";
import {
  ArrowUpward,
  Close,
  Settings,
  People,
  GroupAdd,
  AddCircleOutline,
  Send,
  Help,
  Link,
  ExitToApp,
  Assistant,
} from "@material-ui/icons";
import { AdminPanelSettingsIcon } from "@src/assets/img/icons";
import withStyles from "@material-ui/core/styles/withStyles";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { stringToCWId } from "@clockwise/web-commons/src/util/cw-id.util";

const CWID_PREFIX = "menu-item";
// List Item Text
const YOUR_TEAMS = "Your teams";
const CREATE_A_TEAM = "Create a team";
const UPGRADE = "Upgrade";
const SHARE_WITH_COWORKERS = "Share with coworkers";
const SETTINGS = "Settings";
const HELP_CENTER = "Help center";
const ADMIN_PANEL = "Admin Panel";
const ONE_ON_ONES = "One-on-Ones";
const SCHEDULING_LINKS = "Scheduling links";
const PERSONAL_CALENDAR = "Personal calendar";
const SLACK_INTEGRATION = "Slack integration";
const EVENT_COLORING = "Event coloring";

//////////////////
// COMPONENT
//////////////////
export class ChromeMenuDrawerComponent extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
  }
  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  private closeDrawer = () => {
    this.props.setMenuDrawerOpen(false);
  };

  private openSettingsDialog = (settingsStateKey?: keyof ISettingsState) => {
    this.props.setSettingsDialogOpen(true);
    if (settingsStateKey) {
      this.props.toggleExpanded(settingsStateKey);
    }
  };

  private onClickProfile = () => {
    this.closeDrawer();
    this.props.setActiveViewCalendarProfile(this.props.windowUserEmail);
  };

  private onClickSlackIntegration = () => {
    track(TrackingEvents.CHROME_MENU.CLICK_SLACK);
    this.openSettingsDialog("expandIntegrations");
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderHeader = () => {
    const { viewer, windowUserEmail, classes, isWebExtension } = this.props;
    const org = getCurrentOrg(viewer);
    if (!org || org.personForUserErrorable.__typename === "GraphEntityError") {
      return <span />;
    }
    const person = org.personForUserErrorable;
    return (
      <div className={classes.hero}>
        <IconButton style={s.close.style} onClick={this.closeDrawer} cw-id="menu-close-button">
          <Close {...s.close.icon} />
        </IconButton>
        <AvatarPerson
          person={person}
          orgPerson={null}
          size="large"
          backupCalendarId={windowUserEmail}
          onClick={isWebExtension ? () => this.onClickProfile() : undefined}
          showUserBadge
        />
        <H2 style={s.name} cw-id="menu-user-name">
          {viewer.user.givenName} {viewer.user.familyName}
        </H2>
        <div className={classes.email} cw-id="menu-user-email">
          {windowUserEmail}
        </div>
      </div>
    );
  };

  private renderMenuItem = ({
    label,
    onClick,
    IconComponent,
    hide = false,
    ListItemTextProps = {},
  }: {
    label: string | React.ReactNode;
    onClick: () => void;
    IconComponent: typeof SvgIcon;
    hide?: boolean;
    ListItemTextProps?: MuiListItemTextProps;
  }) => {
    if (hide) {
      return null;
    }
    return (
      <MenuItem
        classes={{ root: this.props.classes.menuItem }}
        onClick={onClick}
        cw-id={stringToCWId(JSON.stringify(label), CWID_PREFIX)}
      >
        <ListItemIcon>
          <IconComponent {...s.menuItemIcon} />
        </ListItemIcon>
        <ListItemText inset primary={label} {...ListItemTextProps} />
      </MenuItem>
    );
  };

  private renderAdminPanel = () => {
    const onAdminPanelClick = () => {
      track(TrackingEvents.CHROME_MENU.CLICK_ADMIN_PANEL);
      window.open(paths.admin, "_blank");
    };

    return this.renderMenuItem({
      label: ADMIN_PANEL,
      onClick: () => {
        onAdminPanelClick();
      },
      IconComponent: AdminPanelSettingsIcon,
    });
  };

  private renderOneOnOnes = () => {
    const onClickOneOnOnes = () => {
      track(TrackingEvents.CHROME_MENU.CLICK_ONE_ON_ONES);
      window.open(paths.oneOnOnes, "_blank");
    };

    return this.renderMenuItem({
      label: ONE_ON_ONES,
      onClick: () => {
        onClickOneOnOnes();
      },
      IconComponent: People,
      ListItemTextProps: {
        secondaryTypographyProps: { style: { display: "flex" } },
        className: this.props.classes.listItemText__new,
      },
    });
  };

  private maybeRenderOptionalMenuItems = () => {
    const { viewer, classes } = this.props;
    const org = getCurrentOrg(viewer);

    if (!org) {
      return <span />;
    }

    // personal calendar
    const sortedDataSources = viewer.user.dataSources
      ? sortDataSources(viewer.user.dataSources.edges, org.domains)
      : null;
    const showPersonalCalendar = sortedDataSources && sortedDataSources.other.length === 0;
    // slack
    const slackAccounts =
      org.slackAccountsErrorable.__typename !== "GraphEntityError" &&
      org.slackAccountsErrorable.list;
    const showSlack = slackAccounts && slackAccounts.length === 0;
    // color coding
    const eventCategoryColorings = filterAndSortCategories(org.eventColoringSettingsErrorable);
    const showColorCoding = eventCategoryColorings.every((ecc) => !ecc.active);

    return (
      <div>
        {showPersonalCalendar || showSlack ? (
          <div>
            <Divider className={classes.divider} />
            <H2 style={{ marginLeft: 15, marginTop: 15 }}>More features</H2>
          </div>
        ) : (
          ""
        )}
        {this.renderMenuItem({
          label: PERSONAL_CALENDAR,
          onClick: () => {
            track(TrackingEvents.CHROME_MENU.CLICK_PERSONAL_CALENDAR);
            this.openSettingsDialog("expandPersonalCalendar");
          },
          IconComponent: AddCircleOutline,
          hide: !showPersonalCalendar,
        })}
        {this.renderMenuItem({
          label: SLACK_INTEGRATION,
          onClick: this.onClickSlackIntegration,
          IconComponent: AddCircleOutline,
          hide: !showSlack,
        })}
        {this.renderMenuItem({
          label: EVENT_COLORING,
          onClick: () => {
            track(TrackingEvents.CHROME_MENU.CLICK_COLORING);
            this.openSettingsDialog("expandColorCoding");
          },
          IconComponent: AddCircleOutline,
          hide: !showColorCoding,
        })}
      </div>
    );
  };

  public render() {
    const t = this.context;
    const { viewer, open, classes, style } = this.props;
    const org = getCurrentOrg(viewer);

    if (!org) {
      return <span />;
    }

    const maybePrimaryTeam = maybeGetPrimaryTeamFromOrg(org);
    const maybePrimaryTeamId = maybePrimaryTeam ? maybePrimaryTeam.teamId : undefined;
    return (
      <ChromeDrawer style={style} open={open}>
        {this.renderHeader()}
        <div className={classes.body}>
          {maybePrimaryTeamId
            ? this.renderMenuItem({
                label: YOUR_TEAMS,
                onClick: () => {
                  track(TrackingEvents.CHROME_MENU.CLICK_YOUR_TEAMS);
                  this.openSettingsDialog("expandTeam");
                },
                IconComponent: People,
              })
            : this.renderMenuItem({
                label: CREATE_A_TEAM,
                onClick: () => {
                  track(TrackingEvents.CHROME_MENU.CLICK_TEAM_CREATE);
                  this.openSettingsDialog("expandTeam");
                },
                IconComponent: GroupAdd,
              })}
          {this.renderMenuItem({
            label: UPGRADE,
            onClick: () => {
              track(TrackingEvents.CHROME_MENU.CLICK_UPGRADE);
              this.props.setPricingDialogOpen(true, maybePrimaryTeamId);
            },
            IconComponent: ArrowUpward,
            hide: !isPrimarySubscriptionFreePlan(org),
          })}
          {this.renderMenuItem({
            label: SHARE_WITH_COWORKERS,
            onClick: () => {
              track(TrackingEvents.CHROME_MENU.CLICK_INVITE);
              this.openSettingsDialog("expandInvites");
            },
            IconComponent: Send,
          })}
          {this.renderMenuItem({
            label: t("drawer:autopilot-nav-item"),
            onClick: () => {
              track(TrackingEvents.CHROME_MENU.CLICK_AUTOPILOT);
              this.closeDrawer();
              this.props.setActiveViewAutopilotList();
            },
            IconComponent: Assistant,
          })}
          {this.renderOneOnOnes()}
          {this.renderMenuItem({
            label: SCHEDULING_LINKS,
            onClick: () => {
              track(TrackingEvents.LINKS.SIDEPANEL.MANAGE_LINKS);
              window.open(paths.schedulingLinks, "_blank");
            },
            IconComponent: Link,
            ListItemTextProps: {
              className: this.props.classes.listItemText__new,
              secondaryTypographyProps: { style: { display: "flex" } },
            },
          })}
          {this.maybeRenderOptionalMenuItems()}
          <Divider className={classes.divider} />
          {this.renderMenuItem({
            label: SETTINGS,
            onClick: () => {
              track(TrackingEvents.CHROME_MENU.CLICK_SETTINGS);
              this.openSettingsDialog();
            },
            IconComponent: Settings,
          })}
          {this.renderAdminPanel()}
          <ChromeFeedback />
          {this.renderMenuItem({
            label: HELP_CENTER,
            onClick: () => {
              track(TrackingEvents.CHROME_MENU.CLICK_HELP);
              window.open(baseUrl, "_blank");
            },
            IconComponent: Help,
          })}
          <LogoutBtn
            cw-id={stringToCWId("Sign out", CWID_PREFIX)}
            isMenuItem
            menuItemIcon={<ExitToApp {...s.menuItemIcon} />}
            onLogout={() => {
              track(TrackingEvents.CHROME_MENU.CLICK_LOGOUT);
              this.closeDrawer();
            }}
          />
        </div>
      </ChromeDrawer>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
const ChromeMenuDrawerStyled = withStyles(s.styles)(ChromeMenuDrawerComponent);

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    windowUserEmail: state.webExtension.windowUserEmail,
    isWebExtension: state.webExtension.isWebExtension,
  };
}

const ChromeMenuDrawerRedux = connect(mapStateToProps, {
  setMenuDrawerOpen,
  setSettingsDialogOpen,
  toggleExpanded,
  setPricingDialogOpen,
  setActiveViewAutopilotList,
  setActiveViewCalendarProfile,
})(ChromeMenuDrawerStyled);

//////////////////
// RELAY
//////////////////
export const ChromeMenuDrawer = createFragmentContainer<IContainer>(
  ChromeMenuDrawerRedux,
  chromeMenuDrawerFragments,
);
